import { QueryClient } from "@tanstack/react-query";
import { createRouter } from "@tanstack/react-router";
import { routeTree } from "./routeTree.gen";
import { captureException, initSentry } from "../sentry.config";

export const getRouter = () => {
  // Sentry must be live before the first render, otherwise errors thrown during
  // module evaluation, provider setup or hydration escape with no handler
  // attached. `getRouter()` is the first app code that runs in the browser, so
  // this is the earliest safe hook. `initSentry()` is idempotent.
  initSentry();

  const queryClient = new QueryClient();

  const router = createRouter({
    routeTree,
    context: { queryClient },
    scrollRestoration: true,
    defaultPreloadStaleTime: 0,

    // Single funnel for every error the router handles: loader rejections,
    // beforeLoad throws, and render errors inside route components.
    //
    // Without this, the root `errorComponent` renders a friendly message and
    // the underlying error is never reported anywhere - a blank screen with
    // zero telemetry. TanStack Router has no `onError` router event, so
    // `defaultOnCatch` is the correct hook (see RouterEvents in
    // @tanstack/router-core: navigation events only).
    defaultOnCatch: (error, errorInfo) => {
      captureException(error, {
        extra: {
          // React puts the component stack here. This is what makes a production
          // minified trace actionable: it names the component that threw.
          componentStack: errorInfo?.componentStack,
        },
        tags: {
          source: "tanstack-router",
        },
      });
    },
  });

  return router;
};
